// src/components/LenderResponseImport.tsx
//
// Admin page for the lender-response DATA import — the import slice of the
// parked Lender Response Import plan (24 Aug 2026). No outcome actions:
// every row records data only; the action layer ships separately.
//
// Flow: drop file + metadata → (pick the TAB when the workbook has several)
// → editable column mapping (auto-suggested; Continue disabled until every
// required field is mapped) → preview counts/problems → explicit confirm →
// background run with live progress → import history below.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ImportMetadata,
  ImportRun,
  Mapping,
  PreviewResult,
  cancelRun,
  commitImport,
  downloadRunFile,
  downloadRunReport,
  getRun,
  listRuns,
  previewImport,
  resumeRun,
} from '../api/lenderResponseImport';
import LenderResponseMultiTab from './LenderResponseMultiTab';

const POLL_MS = 2000;

const emptyMeta: ImportMetadata = { batchLabel: '', receivedDate: '', lender: '' };

const LenderResponseImport: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [meta, setMeta] = useState<ImportMetadata>(emptyMeta);
  const [dragOver, setDragOver] = useState(false);

  const [sheet, setSheet] = useState<string | undefined>(undefined);
  // Password-protected workbook (lenders send these): typed once, sent with
  // every preview and the commit so the server unlocks the same bytes.
  const [password, setPassword] = useState('');
  // Multi-tab import (27 Oct 2026): tabs ticked in step 2, then handed to
  // LenderResponseMultiTab which previews and imports them one by one.
  const [tickedSheets, setTickedSheets] = useState<Set<string>>(new Set());
  const [multiSheets, setMultiSheets] = useState<string[] | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [mapping, setMapping] = useState<Mapping>({});
  const [ignored, setIgnored] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const [overrideClashes, setOverrideClashes] = useState(false);

  const [runId, setRunId] = useState<string | null>(null);
  const [run, setRun] = useState<ImportRun | null>(null);
  const [history, setHistory] = useState<ImportRun[]>([]);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshHistory = useCallback(() => {
    listRuns().then(setHistory).catch(() => undefined);
  }, []);

  useEffect(() => {
    refreshHistory();
  }, [refreshHistory]);

  // Run polling — stops on any terminal status or when the run disappears.
  useEffect(() => {
    if (!runId) return undefined;
    const tick = async () => {
      try {
        const doc = await getRun(runId);
        setRun(doc);
        if (doc.status !== 'running') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          refreshHistory();
        }
      } catch {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    tick();
    pollRef.current = setInterval(tick, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [runId, refreshHistory]);

  const resetFlow = () => {
    setPreview(null);
    setMapping({});
    setIgnored(new Set());
    setSheet(undefined);
    setPassword('');
    setTickedSheets(new Set());
    setMultiSheets(null);
    setError(null);
    setAllowDuplicate(false);
    setOverrideClashes(false);
  };

  const onFile = (f: File | null) => {
    setFile(f);
    resetFlow();
  };

  const changeMeta = (next: ImportMetadata) => {
    setMeta(next);
    // A preview is a confirmation of metadata as well as file bytes.
    // Never leave Apply enabled against values the user has since changed.
    setPreview(null);
    setAllowDuplicate(false);
    setOverrideClashes(false);
  };

  const metadataComplete =
    !!file && !!meta.batchLabel.trim() && !!meta.receivedDate && !!meta.lender.trim();

  const runPreview = async (opts: { sheet?: string; mapping?: Mapping; password?: string } = {}) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const result = await previewImport(file, meta, {
        sheet: opts.sheet ?? sheet,
        mapping: opts.mapping ?? (Object.keys(mapping).length ? mapping : undefined),
        ignoredHeaders: Array.from(ignored),
        password: opts.password ?? password,
      });
      setPreview(result);
      if (result.sheetUsed) setSheet(result.sheetUsed);
      if (result.effectiveMapping) setMapping(result.effectiveMapping);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const remapField = (key: string, header: string) => {
    const next = { ...mapping, [key]: header || null };
    setMapping(next);
    runPreview({ mapping: next });
  };

  const toggleIgnored = (header: string) => {
    const next = new Set(ignored);
    if (next.has(header)) next.delete(header);
    else next.add(header);
    setIgnored(next);
    setPreview(null);
    setAllowDuplicate(false);
  };

  const startCommit = async () => {
    if (!file || !preview?.fileSha256 || !preview.previewToken
        || !preview.mappingOk) return;
    setBusy(true);
    setError(null);
    try {
      const { runId: id } = await commitImport(file, meta, {
        sheet,
        mapping,
        ignoredHeaders: Array.from(ignored),
        expectedSha256: preview.fileSha256,
        expectedPreviewToken: preview.previewToken,
        allowDuplicateFile: allowDuplicate,
        overrideOutcomeClashes: overrideClashes,
        password,
      });
      setRunId(id);
      setRun(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Import failed to start');
    } finally {
      setBusy(false);
    }
  };

  const resume = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await resumeRun(id);
      setRunId(id);
      setRun(null);
      refreshHistory();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Run could not be resumed');
    } finally {
      setBusy(false);
    }
  };

  const counts = preview?.counts;
  const usedHeaders = new Set(
    Object.values(mapping).filter((h): h is string => !!h));
  const otherHeaders = (preview?.fileHeaders || []).filter(
    (h) => !usedHeaders.has(h));

  return (
    <div style={styles.page}>
      <h2 style={styles.h2}>Lender Response Import</h2>
      <p style={styles.note}>
        Imports the data from a lender's returned spreadsheet onto the matched
        claims (the green "Submission Data Received" card). Data only — no
        emails are sent and no claims are closed by this import.
      </p>

      {/* ------------------------------------------------ 1. file + metadata */}
      <section style={styles.card}>
        <h3 style={styles.h3}>1 — File</h3>
        <div
          data-testid="dropzone"
          style={{ ...styles.dropzone, ...(dragOver ? styles.dropzoneOver : null) }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            onFile(e.dataTransfer.files?.[0] || null);
          }}
        >
          {file ? (
            <span><strong>{file.name}</strong> ({Math.ceil(file.size / 1024)} KB)</span>
          ) : (
            <span>Drag a .xlsx or .csv here, or&nbsp;
              <label style={styles.browse}>
                browse
                <input
                  type="file"
                  accept=".xlsx,.csv"
                  style={{ display: 'none' }}
                  onChange={(e) => onFile(e.target.files?.[0] || null)}
                />
              </label>
            </span>
          )}
        </div>
        <div style={styles.metaRow}>
          <label style={styles.metaField}>
            Lender
            <input
              value={meta.lender}
              onChange={(e) => changeMeta({ ...meta, lender: e.target.value })}
              placeholder="e.g. SANTANDER CONSUMER (UK) PLC"
              style={styles.input}
            />
          </label>
          <label style={styles.metaField}>
            Batch label
            <input
              value={meta.batchLabel}
              onChange={(e) => changeMeta({ ...meta, batchLabel: e.target.value })}
              placeholder="e.g. santander-2026-08"
              style={styles.input}
            />
          </label>
          <label style={styles.metaField}>
            Date received
            <input
              type="date"
              value={meta.receivedDate}
              onChange={(e) => changeMeta({ ...meta, receivedDate: e.target.value })}
              style={styles.input}
            />
          </label>
          <button
            onClick={() => runPreview()}
            disabled={!metadataComplete || busy}
            style={styles.primaryBtn}
          >
            {busy ? 'Checking…' : 'Check file & preview'}
          </button>
        </div>
      </section>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* ------------------------------------------------- 2a. password */}
      {preview?.needsPassword && (
        <section style={styles.card}>
          <h3 style={styles.h3}>2 — This workbook is password-protected</h3>
          <p style={styles.note}>
            Enter the password the lender gave and the importer will unlock it.
            The password is only used to read the file; it is not stored.
          </p>
          {preview.passwordError && <div style={styles.errorBanner}>{preview.passwordError}</div>}
          <form
            onSubmit={(e) => { e.preventDefault(); if (password.trim()) runPreview({ password }); }}
            style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
          >
            <input
              type="password"
              aria-label="Workbook password"
              placeholder="Workbook password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, minWidth: 260 }}
            />
            <button type="submit" style={styles.sheetBtn} disabled={busy || !password.trim()}>
              {busy ? 'Unlocking…' : 'Unlock and preview'}
            </button>
          </form>
        </section>
      )}

      {/* ------------------------------------------------- 2. sheet selection */}
      {preview?.needsSheetSelection && !multiSheets && (
        <section style={styles.card}>
          <h3 style={styles.h3}>2 — Which tab?</h3>
          <p style={styles.note}>
            This workbook has {preview.sheets?.length} tabs — pick the one to
            import. Nothing is read until you choose.
          </p>
          <div style={styles.sheetList}>
            {(preview.sheets || []).map((name) => (
              <button
                key={name}
                style={styles.sheetBtn}
                onClick={() => { setSheet(name); runPreview({ sheet: name }); }}
              >
                {name}
              </button>
            ))}
          </div>
          <h4 style={styles.h4}>Or import several tabs together</h4>
          <p style={styles.note}>
            Tick the tabs and they are checked and imported one after another, each as its own run.
          </p>
          <div style={styles.otherCols}>
            {(preview.sheets || []).map((name) => (
              <label key={name} style={styles.otherCol}>
                <input
                  type="checkbox"
                  checked={tickedSheets.has(name)}
                  onChange={(e) => {
                    const next = new Set(tickedSheets);
                    if (e.target.checked) next.add(name); else next.delete(name);
                    setTickedSheets(next);
                  }}
                />
                {name}
              </label>
            ))}
          </div>
          <button
            type="button"
            style={styles.primaryBtn}
            disabled={tickedSheets.size === 0}
            onClick={() => setMultiSheets((preview.sheets || []).filter((n) => tickedSheets.has(n)))}
          >
            Check {tickedSheets.size || ''} selected tab{tickedSheets.size === 1 ? '' : 's'}
          </button>
        </section>
      )}

      {multiSheets && file && (
        <LenderResponseMultiTab
          file={file}
          meta={meta}
          password={password}
          sheets={multiSheets}
          onBack={() => { setMultiSheets(null); }}
          onFinished={refreshHistory}
        />
      )}

      {(preview?.headerErrors?.length ?? 0) > 0 && (
        <div style={styles.errorBanner}>
          {preview!.headerErrors!.map((e) => <div key={e}>{e}</div>)}
          <div>Nothing has been imported.</div>
        </div>
      )}

      {/* ---------------------------------------------------- 3. column mapping */}
      {preview?.fileHeaders && (
        <section style={styles.card}>
          <h3 style={styles.h3}>
            {preview.sheets && preview.sheets.length > 1
              ? `3 — Column mapping (tab: ${preview.sheetUsed})`
              : '2 — Column mapping'}
          </h3>
          {preview.mappingOk ? (
            <div style={styles.okBanner}>
              All required fields are mapped.
            </div>
          ) : (
            <div style={styles.warnBanner}>
              {(preview.mappingErrors || []).map((e) => <div key={e}>{e}</div>)}
              <div>Nothing will be imported until every required field is mapped.</div>
            </div>
          )}
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Our field</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Found in file as</th>
              </tr>
            </thead>
            <tbody>
              {(preview.vocabulary || [])
                .filter((f) => f.required || mapping[f.key])
                .map((f) => (
                  <tr key={f.key}>
                    <td style={styles.td}>
                      {f.label}
                      {f.required && <span style={styles.requiredStar}> *</span>}
                    </td>
                    <td style={styles.td}>
                      <span style={mapping[f.key] ? styles.pillOk : styles.pillMissing}>
                        {mapping[f.key] ? 'Mapped' : 'Not mapped'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <select
                        value={mapping[f.key] || ''}
                        onChange={(e) => remapField(f.key, e.target.value)}
                        style={styles.select}
                      >
                        <option value="">— not in this file —</option>
                        {(preview.fileHeaders || []).map((h) => (
                          <option
                            key={h}
                            value={h}
                            disabled={usedHeaders.has(h) && mapping[f.key] !== h}
                          >
                            {h}{usedHeaders.has(h) && mapping[f.key] !== h
                              ? ' (already mapped)' : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>

          {otherHeaders.length > 0 && (
            <>
              <h4 style={styles.h4}>Other columns in the file</h4>
              <p style={styles.note}>
                Kept as extra data on each claim unless ignored.
              </p>
              <div style={styles.otherCols}>
                {otherHeaders.map((h) => (
                  <label key={h} style={styles.otherCol}>
                    <input
                      type="checkbox"
                      checked={!ignored.has(h)}
                      onChange={() => { toggleIgnored(h); }}
                    />
                    {h}{ignored.has(h) ? ' (ignored)' : ''}
                  </label>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* --------------------------------------------------------- 4. preview */}
      {counts && (
        <section style={styles.card}>
          <h3 style={styles.h3}>Preview</h3>
          <div style={styles.countsGrid}>
            <Count label="Rows" value={counts.totalRows} />
            <Count label="Claims" value={counts.uniqueClaims} />
            <Count label="Matched (live)" value={counts.matchedLive} />
            <Count label="Matched (cancelled)" value={counts.matchedCancelled} />
            <Count label="Not found" value={counts.unmatched} warn />
            <Count label="Outcome clashes" value={counts.outcomeClashes} warn />
            <Count label="Replacing this batch" value={counts.claimsReplacingExistingSource} />
            <Count label="Agreement № promotions" value={counts.agreementNumberPromotions} />
          </div>
          <div style={styles.note}>
            Outcomes:{' '}
            {Object.entries(counts.byOutcome)
              .map(([k, v]) => `${k} (${v})`)
              .join(' · ') || '—'}
            {' · '}Source: <code>{preview?.effectiveSource}</code>
          </div>

          {preview?.duplicateOfRun && (
            <div style={styles.warnBanner}>
              <strong>This exact file was already imported</strong> — run{' '}
              {preview.duplicateOfRun.runId} by {preview.duplicateOfRun.startedBy}{' '}
              on {preview.duplicateOfRun.startedAt?.slice(0, 10)}{' '}
              ({preview.duplicateOfRun.status}).
              <label style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={allowDuplicate}
                  onChange={(e) => setAllowDuplicate(e.target.checked)}
                />
                Import again anyway
              </label>
            </div>
          )}

          {(preview?.problems?.unmatched.length ?? 0) > 0 && (
            <details style={styles.problem}>
              <summary>
                {preview!.problems!.unmatched.length} reference(s) not found —
                these rows will be reported, not imported
              </summary>
              <div style={styles.problemBody}>
                {preview!.problems!.unmatched.join(', ')}
              </div>
            </details>
          )}
          {(preview?.problems?.outcomeClashes.length ?? 0) > 0 && (
            <details style={styles.problem}>
              <summary>
                {preview!.problems!.outcomeClashes.length} outcome clash(es) —
                skipped unless overridden
              </summary>
              <div style={styles.problemBody}>
                {preview!.problems!.outcomeClashes.map((c) => (
                  <div key={c.claimId}>
                    {c.claimId}: has “{c.existing}”, file says “{c.incoming}”
                  </div>
                ))}
                <label style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={overrideClashes}
                    onChange={(e) => setOverrideClashes(e.target.checked)}
                  />
                  Overwrite clashing outcomes
                </label>
              </div>
            </details>
          )}
          {(preview?.problems?.cancelledClaims.length ?? 0) > 0 && (
            <details style={styles.problem}>
              <summary>
                {preview!.problems!.cancelledClaims.length} cancelled claim(s) —
                data is written and flagged, nothing else happens
              </summary>
              <div style={styles.problemBody}>
                {preview!.problems!.cancelledClaims.join(', ')}
              </div>
            </details>
          )}

          <button
            onClick={startCommit}
            disabled={
              busy || !preview?.mappingOk ||
              (!!preview?.duplicateOfRun && !allowDuplicate)
            }
            style={styles.primaryBtn}
          >
            Apply — import the data
          </button>
        </section>
      )}

      {/* -------------------------------------------------------- 5. progress */}
      {run && (
        <section style={styles.card}>
          <h3 style={styles.h3}>Run {run.id}</h3>
          <div style={styles.progressOuter}>
            <div style={{ ...styles.progressInner, width: `${run.percent ?? 0}%` }} />
          </div>
          <div style={styles.note}>
            {run.status === 'running'
              ? `${run.processed ?? 0} of ${run.totals?.claims ?? '?'} claims — safe to close this page; the import continues on the server.`
              : `Finished: ${run.status}`}
            {run.stale ? ' (worker heartbeat is stale)' : ''}
          </div>
          {run.counts && (
            <div style={styles.note}>
              written {run.counts.written} · not found {run.counts.notFound} ·
              clashes skipped {run.counts.clashSkipped} · errors {run.counts.errors}
            </div>
          )}
          {run.status === 'running' && (
            <>
              <button style={styles.secondaryBtn} onClick={() => cancelRun(run.id)}>
                Cancel run
              </button>
              {run.stale && (
                <button disabled={busy} style={styles.secondaryBtn}
                  onClick={() => resume(run.id)}>
                  Resume stale run
                </button>
              )}
            </>
          )}
          {run.status !== 'running' && (
            <button style={styles.secondaryBtn} onClick={() => downloadRunReport(run.id)}>
              Download results report
            </button>
          )}
        </section>
      )}

      {/* --------------------------------------------------------- 6. history */}
      <section style={styles.card}>
        <h3 style={styles.h3}>Import history</h3>
        {history.length === 0 ? (
          <p style={styles.note}>No imports yet.</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Lender</th>
                <th style={styles.th}>Batch</th>
                <th style={styles.th}>Tab</th>
                <th style={styles.th}>Who</th>
                <th style={styles.th}>Claims</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Files</th>
              </tr>
            </thead>
            <tbody>
              {history.map((r) => (
                <tr key={r.id}>
                  <td style={styles.td}>{r.startedAt?.slice(0, 16).replace('T', ' ')}</td>
                  <td style={styles.td}>{r.lender}</td>
                  <td style={styles.td}>{r.batchLabel}</td>
                  <td style={styles.td}>{r.sheet}</td>
                  <td style={styles.td}>{r.startedBy}</td>
                  <td style={styles.td}>
                    {r.counts?.written ?? 0}/{r.totals?.claims ?? 0}
                  </td>
                  <td style={styles.td}>{r.status}</td>
                  <td style={styles.td}>
                    <button style={styles.linkBtn} onClick={() => downloadRunFile(r.id)}>
                      spreadsheet
                    </button>
                    {' · '}
                    <button style={styles.linkBtn} onClick={() => downloadRunReport(r.id)}>
                      report
                    </button>
                    {r.stale && (
                      <>
                        {' · '}
                        <button disabled={busy} style={styles.linkBtn}
                          onClick={() => resume(r.id)}>
                          resume
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export const Count: React.FC<{ label: string; value: number; warn?: boolean }> = ({
  label, value, warn,
}) => (
  <div style={styles.count}>
    <div style={{
      ...styles.countValue,
      color: warn && value > 0 ? 'var(--color-error-600)' : 'var(--color-primary-700)',
    }}>
      {value}
    </div>
    <div style={styles.countLabel}>{label}</div>
  </div>
);

export const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1100, margin: '0 auto', padding: 16 },
  h2: { margin: '0 0 4px' },
  h3: { margin: '0 0 10px' },
  h4: { margin: '16px 0 4px' },
  note: { color: 'var(--color-neutral-600)', fontSize: 'var(--text-sm)', margin: '4px 0 10px' },
  card: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 10,
    padding: 16,
    marginBottom: 16,
    background: 'var(--color-neutral-0)',
  },
  dropzone: {
    border: '2px dashed var(--color-neutral-300)',
    borderRadius: 8,
    padding: 28,
    textAlign: 'center',
    color: 'var(--color-neutral-600)',
    marginBottom: 12,
  },
  dropzoneOver: {
    borderColor: 'var(--color-primary-500)',
    background: 'var(--color-primary-50)',
  },
  browse: {
    color: 'var(--color-primary-600)',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  metaRow: { display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' },
  metaField: {
    display: 'flex', flexDirection: 'column', gap: 4,
    fontSize: 'var(--text-sm)', fontWeight: 600,
  },
  input: {
    padding: '7px 10px', border: '1px solid var(--color-neutral-300)',
    borderRadius: 6, minWidth: 210, fontWeight: 400,
  },
  select: {
    padding: '5px 8px', border: '1px solid var(--color-neutral-300)',
    borderRadius: 6, maxWidth: 320,
  },
  primaryBtn: {
    padding: '9px 18px', border: 'none', borderRadius: 6,
    background: 'var(--color-primary-600)', color: '#fff',
    fontWeight: 700, cursor: 'pointer',
  },
  secondaryBtn: {
    padding: '7px 14px', borderRadius: 6, cursor: 'pointer',
    border: '1px solid var(--color-neutral-300)', background: 'transparent',
  },
  linkBtn: {
    border: 'none', background: 'none', padding: 0, cursor: 'pointer',
    color: 'var(--color-primary-600)', textDecoration: 'underline',
  },
  errorBanner: {
    background: 'var(--color-error-50)', color: 'var(--color-error-700)',
    border: '1px solid var(--color-error-100)', borderRadius: 8,
    padding: '10px 14px', marginBottom: 16,
  },
  warnBanner: {
    background: 'var(--color-warning-50)', color: '#92400e',
    border: '1px solid var(--color-warning-100)', borderRadius: 8,
    padding: '10px 14px', margin: '10px 0',
  },
  okBanner: {
    background: 'var(--color-success-50)', color: 'var(--color-success-700)',
    border: '1px solid var(--color-success-100)', borderRadius: 8,
    padding: '10px 14px', margin: '10px 0',
  },
  sheetList: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  sheetBtn: {
    padding: '10px 22px', borderRadius: 8, cursor: 'pointer',
    border: '2px solid var(--color-primary-300)',
    background: 'var(--color-primary-50)', fontWeight: 700,
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' },
  th: {
    textAlign: 'left', padding: '6px 10px',
    borderBottom: '2px solid var(--color-neutral-200)',
    color: 'var(--color-neutral-600)',
  },
  td: { padding: '6px 10px', borderBottom: '1px solid var(--color-neutral-100)' },
  requiredStar: { color: 'var(--color-error-600)' },
  pillOk: {
    background: 'var(--color-success-100)', color: 'var(--color-success-700)',
    borderRadius: 999, padding: '2px 10px', fontSize: 'var(--text-xs)', fontWeight: 700,
  },
  pillMissing: {
    background: 'var(--color-error-100)', color: 'var(--color-error-700)',
    borderRadius: 999, padding: '2px 10px', fontSize: 'var(--text-xs)', fontWeight: 700,
  },
  otherCols: { display: 'flex', gap: 14, flexWrap: 'wrap' },
  otherCol: { display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--text-sm)' },
  countsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: 10, marginBottom: 10,
  },
  count: {
    background: 'var(--color-neutral-50)', borderRadius: 8, padding: '10px 12px',
    textAlign: 'center',
  },
  countValue: { fontSize: 22, fontWeight: 800 },
  countLabel: { fontSize: 'var(--text-xs)', color: 'var(--color-neutral-600)' },
  problem: { margin: '8px 0' },
  problemBody: {
    maxHeight: 180, overflowY: 'auto', padding: '8px 4px',
    fontSize: 'var(--text-sm)', color: 'var(--color-neutral-700)',
  },
  checkboxRow: {
    display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, fontWeight: 700,
  },
  progressOuter: {
    height: 12, borderRadius: 999, background: 'var(--color-neutral-100)',
    overflow: 'hidden', margin: '8px 0',
  },
  progressInner: {
    height: '100%', background: 'var(--color-primary-500)',
    transition: 'width 400ms ease',
  },
};

export default LenderResponseImport;
